import React, { useState, useEffect } from 'react';
import { FunctionComponent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch } from 'src/redux/store';
import {
    Grid,
    TextField,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Autocomplete,
    InputAdornment,
    Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';

import { ReactComponent as MemberIcon } from 'src/assets/icons/member.svg';
import { ReactComponent as AdminIcon } from 'src/assets/icons/admin.svg';
import { ReactComponent as RemoveIcon } from 'src/assets/icons/remove.svg';

import {
    TitleText,
    StyledButton,
    ButtonTextBox,
    ButtonIconBox
} from '../../styled';

import {
    TeamServicesCard,
    TeamTitleBox,
    TeamIconButton,
    TeamTitleTypography,
    TeamLabelTypography,
    TeamMemberNameTypography,
    TeamMemberEmailTypography,
    StyledDivider,
    ButtonContentBox,
    StyledAutocomplete,
    StyledAutocompleteOption,
    StyledAutocompletePaper,
    StyledFormControl,
    StyledSelect,
    StyledSelectOption,
    StyledSelectText
} from './styled';

import color from 'src/theme/color';

import { getUsersListFromState } from '../../../../../redux/onboarding/selectors';
import { getUsers } from 'src/redux/onboarding/service';
import { GetUsersRequest } from 'src/types/onboarding';
import { inviteProjectUser } from 'src/redux/user/service';
import { TeamMember, User } from '../../../../../types/projects';
import isValidEmail from 'src/utils/validateEmail';
import ActionDialog from 'src/shared/ActionDialog';

interface ProjectDetailsProps {
    setCurrentStep: (step: number) => void;
    teamMembers: TeamMember[];
    setTeamMembers: (members: TeamMember[]) => void;
    projectId: number;
}

const TeamConfiguration: FunctionComponent<ProjectDetailsProps> = ({
    setCurrentStep,
    teamMembers,
    setTeamMembers,
    projectId
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const userList = useSelector(getUsersListFromState);

    const [isSaveHovered, setSaveIsHovered] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
    const [invitedEmail, setInvitedEmail] = useState('');

    useEffect(() => {
        const request: GetUsersRequest = {
            query: searchQuery
        };
        dispatch(getUsers(request));
    }, [searchQuery, dispatch]);

    const handleUserSelect = async (
        event: React.SyntheticEvent,
        value: User | null
    ) => {
        if (value) {
            const exists = teamMembers.some(
                (member) => member.email === value.email
            );
            if (!exists) {
                setTeamMembers([
                    ...teamMembers,
                    {
                        id: value.id,
                        name: value.name,
                        email: value.email,
                        role: 'Member'
                    }
                ]);
            }

            setSearchQuery('');
        }
    };

    const handleInputChange = (
        event: React.SyntheticEvent,
        newInputValue: string
    ) => {
        setSearchQuery(newInputValue);

        if (isValidEmail(newInputValue)) {
            setInviteEmail(newInputValue);
        } else {
            setInviteEmail('');
        }
    };

    const handleRoleChange = (index: number, newRole: TeamMember['role']) => {
        const updatedMembers = [...teamMembers];
        updatedMembers[index].role = newRole;
        setTeamMembers(updatedMembers);
    };

    const getRoleIcon = (role: TeamMember['role']) => {
        switch (role) {
            case 'Member':
                return <MemberIcon width={18} height={18} />;
            case 'Admin':
                return <AdminIcon width={18} height={18} />;
            case 'Remove':
                return <RemoveIcon width={18} height={18} />;
        }
    };

    const filterAvailableUsers = (options: User[]) => {
        if (!options) return [];
        return options.filter(
            (user) => !teamMembers.some((member) => member.email === user.email)
        );
    };

    const handleInvitationDialogClose = () => {
        setInvitationDialogOpen(false);
        setInvitedEmail('');
    };

    return (
        <TeamServicesCard>
            <TeamTitleBox>
                <TeamIconButton
                    aria-label="back"
                    onClick={() => setCurrentStep(2)}
                >
                    <ArrowBackIcon fontSize="small" />
                </TeamIconButton>
                <TeamTitleTypography
                    variant="body1"
                    component={TitleText as React.ElementType}
                >
                    Team members
                </TeamTitleTypography>
            </TeamTitleBox>
            <StyledDivider />

            <TeamLabelTypography>Add people</TeamLabelTypography>

            <StyledAutocomplete>
                <Autocomplete
                    options={filterAvailableUsers(userList || [])}
                    getOptionLabel={(option) => option.name}
                    onChange={handleUserSelect}
                    onInputChange={handleInputChange}
                    inputValue={searchQuery}
                    clearOnBlur={false}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            variant="outlined"
                            placeholder="Search users or enter email..."
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <StyledAutocompleteOption>
                                {option.name}
                            </StyledAutocompleteOption>
                        </li>
                    )}
                    PaperComponent={(props) => (
                        <StyledAutocompletePaper {...props} />
                    )}
                    noOptionsText="No users found"
                    filterOptions={(options) => options}
                />
            </StyledAutocomplete>

            <TeamLabelTypography
                sx={{
                    marginTop: 3
                }}
            >
                People with access
            </TeamLabelTypography>

            <StyledDivider />

            {teamMembers.length > 0 ? (
                teamMembers.map((member, index) => (
                    <React.Fragment key={member.email}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={7}>
                                <TeamMemberNameTypography>
                                    {member.name}
                                </TeamMemberNameTypography>
                                <TeamMemberEmailTypography>
                                    {member.email}
                                </TeamMemberEmailTypography>
                            </Grid>
                            <Grid item>
                                <StyledFormControl
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                >
                                    <StyledSelect
                                        value={member.role}
                                        onChange={(e) =>
                                            handleRoleChange(
                                                index,
                                                e.target
                                                    .value as TeamMember['role']
                                            )
                                        }
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor:
                                                        color.blackLight2,
                                                    color: color.grey,
                                                    boxShadow: 'none',
                                                    '& .MuiMenuItem-root': {
                                                        paddingTop: '4px',
                                                        paddingBottom: '4px'
                                                    }
                                                }
                                            }
                                        }}
                                        renderValue={(selected) => (
                                            <StyledSelectOption>
                                                {getRoleIcon(
                                                    selected as TeamMember['role']
                                                )}
                                                <StyledSelectText>
                                                    {
                                                        selected as TeamMember['role']
                                                    }
                                                </StyledSelectText>
                                            </StyledSelectOption>
                                        )}
                                    >
                                        {['Member', 'Admin', 'Remove'].map(
                                            (role) => (
                                                <MenuItem
                                                    key={role}
                                                    value={role}
                                                    sx={{
                                                        backgroundColor:
                                                            color.blackLight2,
                                                        '&:hover': {
                                                            backgroundColor: `${color.blackLight2}CC`
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor:
                                                                color.blackLight2
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            color: color.grey,
                                                            minWidth:
                                                                '40px !important'
                                                        }}
                                                    >
                                                        {getRoleIcon(
                                                            role as TeamMember['role']
                                                        )}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={role}
                                                        sx={{
                                                            '& .MuiTypography-root':
                                                                {
                                                                    margin: 0,
                                                                    fontSize:
                                                                        '14px'
                                                                }
                                                        }}
                                                    />
                                                </MenuItem>
                                            )
                                        )}
                                    </StyledSelect>
                                </StyledFormControl>
                            </Grid>
                        </Grid>
                        <StyledDivider />
                    </React.Fragment>
                ))
            ) : (
                <Typography
                    variant="body2"
                    sx={{
                        textAlign: 'left',
                        fontStyle: 'italic',
                        color: color.white,
                        padding: '12px'
                    }}
                >
                    No one has access yet
                </Typography>
            )}
            <ButtonContentBox>
                <StyledButton
                    variant="contained"
                    fullWidth
                    onClick={() => {
                        if (inviteEmail) {
                            try {
                                dispatch(
                                    inviteProjectUser({
                                        projectId: projectId,
                                        email: inviteEmail
                                    })
                                ).unwrap();
                                setInvitedEmail(inviteEmail);
                                setInvitationDialogOpen(true);
                                setSearchQuery('');
                                setInviteEmail('');
                            } catch (error) {
                                console.log(error);
                            }
                        } else {
                            const activemembers = teamMembers.filter(
                                (member) => member.role !== 'Remove'
                            );
                            setTeamMembers(activemembers);
                            setCurrentStep(2);
                        }
                    }}
                    onMouseEnter={() => setSaveIsHovered(true)}
                    onMouseLeave={() => setSaveIsHovered(false)}
                >
                    <ButtonTextBox $isHovered={isSaveHovered}>
                        {inviteEmail ? 'Invite' : 'Save'}
                    </ButtonTextBox>
                    <ButtonIconBox $isHovered={isSaveHovered}>🐝</ButtonIconBox>
                </StyledButton>
            </ButtonContentBox>
            <ActionDialog
                open={invitationDialogOpen}
                onClose={handleInvitationDialogClose}
                email={invitedEmail}
                mode="invitation"
            />
        </TeamServicesCard>
    );
};

export default TeamConfiguration;
